import React, { useState, useMemo } from "react";
import {
  Box,
  Popover,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
  Switch,
  FormControlLabel,
  useTheme,
  Select,
  MenuItem,
  Stack,
} from "@mui/material";
import {
  Notifications as NotificationsIcon,
  OpenInNew as OpenInNewIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { formatDateTime } from "../../utils/dateFormatter";
import { getCaseStateLabel, CaseState } from "../../types/case";
import type { Case } from "../../types/case";
import { useCaseInbox } from "../../hooks/useCases";

interface NotificationItem {
  id: string;
  type: "assignment" | "status_change" | "comment" | "due_date" | "escalation";
  caseId: number;
  caseTitle: string;
  message: string;
  timestamp: string;
  isRead: boolean;
  priority?: string;
  status?: CaseState;
  actorName?: string;
  rule?: string; // Rule name or ID
  chapter?: string; // Chapter name
}

interface NotificationPanelProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

const getNotificationColor = (type: string) => {
  switch (type) {
    case "assignment":
      return "#3B82F6"; // Blue
    case "status_change":
      return "#10B981"; // Green
    case "comment":
      return "#F59E0B"; // Orange
    case "escalation":
      return "#EF4444"; // Red
    case "due_date":
      return "#8B5CF6"; // Purple
    default:
      return "#6B7280"; // Gray
  }
};

const getNotificationIcon = (type: string) => {
  switch (type) {
    case "assignment":
      return "👤";
    case "status_change":
      return "📋";
    case "comment":
      return "💬";
    case "escalation":
      return "⚠️";
    case "due_date":
      return "📅";
    default:
      return "🔔";
  }
};

// Determine the most relevant notification for a case based on its current state
const classifyCase = (
  caseItem: Case,
): { type: NotificationItem["type"]; message: string } => {
  const state = caseItem.state as CaseState;

  // Escalated cases are highest priority
  if (
    state === CaseState.Escalated ||
    state === CaseState.ComplianceEscalated
  ) {
    return { type: "escalation", message: "Case has been escalated" };
  }

  // Overdue or approaching due date
  if (caseItem.dueDate) {
    const dueDate = new Date(caseItem.dueDate);
    const now = new Date();
    const daysUntilDue = Math.ceil(
      (dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (daysUntilDue < 0) {
      return {
        type: "due_date",
        message: `Overdue by ${Math.abs(daysUntilDue)} day${Math.abs(daysUntilDue) !== 1 ? "s" : ""}`,
      };
    }
    if (daysUntilDue <= 7) {
      return {
        type: "due_date",
        message:
          daysUntilDue === 0
            ? "Due today"
            : `Due in ${daysUntilDue} day${daysUntilDue !== 1 ? "s" : ""}`,
      };
    }
  }

  // Assigned cases
  if (caseItem.assignedTo) {
    const assigneeName =
      caseItem.assignedTo.name ||
      `${caseItem.assignedTo.firstName} ${caseItem.assignedTo.lastName}`.trim() ||
      caseItem.assignedTo.email ||
      "someone";
    return {
      type: "assignment",
      message: `Assigned to ${assigneeName}`,
    };
  }

  // Cases with comments activity
  if (caseItem.commentCount && caseItem.commentCount > 0) {
    return {
      type: "comment",
      message: `${caseItem.commentCount} comment${caseItem.commentCount !== 1 ? "s" : ""}`,
    };
  }

  // Non-open status → status notification
  if (state !== CaseState.Open) {
    return {
      type: "status_change",
      message: `Status: ${getCaseStateLabel(state)}`,
    };
  }

  // Default: new / open case
  return { type: "status_change", message: "New case created" };
};

// Transform cases to notification items — one notification per case
const transformCasesToNotifications = (cases: Case[]): NotificationItem[] => {
  return cases.map((caseItem) => {
    const { type, message } = classifyCase(caseItem);

    const rule =
      caseItem.complianceNodeTitle ||
      (caseItem.sourceRuleId ? `Rule ${caseItem.sourceRuleId}` : undefined);

    return {
      id: `${caseItem.id}-${type}`,
      type,
      caseId: caseItem.id,
      caseTitle: caseItem.title,
      message,
      timestamp: caseItem.updatedAt || caseItem.createdAt,
      isRead: false,
      priority: caseItem.priority,
      status: caseItem.state as CaseState,
      rule,
    };
  });
};

export const NotificationPanel: React.FC<NotificationPanelProps> = ({
  anchorEl,
  open,
  onClose,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Reuse cached TanStack Query data from dashboard — no extra API call
  const { data: inboxData, isLoading: loading } = useCaseInbox(
    {},
    { enabled: open },
  );

  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [filterType, setFilterType] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "priority">(
    "newest",
  );

  // TODO: Remove localStorage implementation once BE API for marking notifications as read is available
  const [readNotifications, setReadNotifications] = useState<Set<string>>(
    () => {
      const stored = localStorage.getItem("readNotifications");
      return stored ? new Set(JSON.parse(stored)) : new Set();
    },
  );

  // Derive notifications from cached cases data
  const notifications = useMemo(() => {
    const cases = inboxData?.cases ?? [];
    const items = transformCasesToNotifications(cases);
    // Apply read status from localStorage
    return items.map((n) => ({
      ...n,
      isRead: readNotifications.has(n.id),
    }));
  }, [inboxData?.cases, readNotifications]);

  const handleNotificationClick = (notification: NotificationItem) => {
    // TODO: Remove localStorage logic once BE API for marking as read is available
    const updatedReadNotifications = new Set(readNotifications);
    updatedReadNotifications.add(notification.id);
    setReadNotifications(updatedReadNotifications);
    localStorage.setItem(
      "readNotifications",
      JSON.stringify(Array.from(updatedReadNotifications)),
    );

    navigate(`/cases/${notification.caseId}`);
    onClose();
  };

  const handleMarkAllAsRead = () => {
    // TODO: Remove localStorage logic once BE API for marking all as read is available
    const allIds = new Set(notifications.map((n) => n.id));
    const merged = new Set([...readNotifications, ...allIds]);
    setReadNotifications(merged);
    localStorage.setItem(
      "readNotifications",
      JSON.stringify(Array.from(merged)),
    );
  };

  // Apply filters and sorting
  let filteredNotifications = notifications;

  // Filter by read/unread
  if (showUnreadOnly) {
    filteredNotifications = filteredNotifications.filter((n) => !n.isRead);
  }

  // Filter by type
  if (filterType !== "all") {
    filteredNotifications = filteredNotifications.filter(
      (n) => n.type === filterType,
    );
  }

  // Apply sorting
  filteredNotifications = [...filteredNotifications].sort((a, b) => {
    switch (sortBy) {
      case "newest":
        return (
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );
      case "oldest":
        return (
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
      case "priority": {
        const priorityOrder = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };
        const aPriority =
          priorityOrder[a.priority as keyof typeof priorityOrder] || 0;
        const bPriority =
          priorityOrder[b.priority as keyof typeof priorityOrder] || 0;
        return bPriority - aPriority;
      }
      default:
        return 0;
    }
  });

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      PaperProps={{
        sx: {
          width: { xs: "100vw", sm: 420 },
          maxWidth: "100%",
          maxHeight: { xs: "80vh", sm: 600 },
          mt: 1,
          boxShadow: theme.shadows[8],
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, fontSize: "1.125rem" }}
          >
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Chip
              label={unreadCount}
              size="small"
              color="primary"
              sx={{ height: 20, fontSize: "0.75rem" }}
            />
          )}
        </Box>
      </Box>

      {/* Filter & Sort Controls */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "rgba(0, 0, 0, 0.01)",
        }}
      >
        <Stack spacing={1.5}>
          {/* Top Row: Unread toggle and Mark all */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showUnreadOnly}
                  onChange={(e) => setShowUnreadOnly(e.target.checked)}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{ fontSize: "0.875rem", fontWeight: 500 }}
                >
                  Only show unread
                </Typography>
              }
              sx={{ m: 0 }}
            />

            {unreadCount > 0 && (
              <Button
                size="small"
                onClick={handleMarkAllAsRead}
                sx={{
                  fontSize: "0.75rem",
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: "auto",
                }}
              >
                Mark all as read
              </Button>
            )}
          </Box>

          {/* Bottom Row: Filter by type and Sort */}
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "stretch",
            }}
          >
            {/* Filter by Type */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 0.5,
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                <FilterListIcon
                  sx={{ fontSize: "0.75rem", mr: 0.5, verticalAlign: "middle" }}
                />
                Filter
              </Typography>
              <Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                size="small"
                fullWidth
                sx={{
                  fontSize: "0.8125rem",
                  "& .MuiSelect-select": {
                    py: 0.75,
                  },
                }}
              >
                <MenuItem value="all" sx={{ fontSize: "0.8125rem" }}>
                  All Types
                </MenuItem>
                <MenuItem value="assignment" sx={{ fontSize: "0.8125rem" }}>
                  👤 Assignments
                </MenuItem>
                <MenuItem value="status_change" sx={{ fontSize: "0.8125rem" }}>
                  📋 Status Changes
                </MenuItem>
                <MenuItem value="comment" sx={{ fontSize: "0.8125rem" }}>
                  💬 Comments
                </MenuItem>
                <MenuItem value="escalation" sx={{ fontSize: "0.8125rem" }}>
                  ⚠️ Escalations
                </MenuItem>
                <MenuItem value="due_date" sx={{ fontSize: "0.8125rem" }}>
                  📅 Due Dates
                </MenuItem>
              </Select>
            </Box>

            {/* Sort By */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 0.5,
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                <SortIcon
                  sx={{ fontSize: "0.75rem", mr: 0.5, verticalAlign: "middle" }}
                />
                Sort
              </Typography>
              <Select
                value={sortBy}
                onChange={(e) =>
                  setSortBy(e.target.value as "newest" | "oldest" | "priority")
                }
                size="small"
                fullWidth
                sx={{
                  fontSize: "0.8125rem",
                  "& .MuiSelect-select": {
                    py: 0.75,
                  },
                }}
              >
                <MenuItem value="newest" sx={{ fontSize: "0.8125rem" }}>
                  Newest First
                </MenuItem>
                <MenuItem value="oldest" sx={{ fontSize: "0.8125rem" }}>
                  Oldest First
                </MenuItem>
                <MenuItem value="priority" sx={{ fontSize: "0.8125rem" }}>
                  By Priority
                </MenuItem>
              </Select>
            </Box>
          </Box>
        </Stack>
      </Box>

      {/* Notifications List */}
      <Box sx={{ overflowY: "auto", flex: 1 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 6,
            }}
          >
            <CircularProgress size={40} />
          </Box>
        ) : filteredNotifications.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
              px: 3,
              textAlign: "center",
            }}
          >
            <NotificationsIcon
              sx={{ fontSize: 64, color: "text.disabled", mb: 2 }}
            />
            <Typography variant="h6" gutterBottom>
              No notifications
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {showUnreadOnly
                ? "You're all caught up! No unread notifications."
                : "You don't have any notifications at the moment."}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Results count */}
            <Box
              sx={{
                px: 3,
                py: 1.5,
                bgcolor: "rgba(0, 0, 0, 0.02)",
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.75rem" }}
              >
                Showing {filteredNotifications.length} of {notifications.length}{" "}
                notifications
              </Typography>
            </Box>

            <List sx={{ p: 0 }}>
              {filteredNotifications.map((notification, index) => (
                <React.Fragment key={notification.id}>
                  <ListItem
                    alignItems="flex-start"
                    sx={{
                      px: 3,
                      py: 2.5,
                      cursor: "pointer",
                      bgcolor: notification.isRead
                        ? "transparent"
                        : "rgba(59, 130, 246, 0.08)",
                      "&:hover": {
                        bgcolor: "rgba(59, 130, 246, 0.12)",
                      },
                      position: "relative",
                      borderLeft: !notification.isRead ? 4 : 0,
                      borderColor: "primary.main",
                      transition: "all 0.2s ease",
                    }}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <ListItemAvatar sx={{ minWidth: 56, mt: 0.5 }}>
                      <Avatar
                        sx={{
                          width: 44,
                          height: 44,
                          bgcolor: getNotificationColor(notification.type),
                          fontSize: "1.375rem",
                        }}
                      >
                        {getNotificationIcon(notification.type)}
                      </Avatar>
                    </ListItemAvatar>

                    <ListItemText
                      disableTypography
                      primary={
                        <Box>
                          {/* Case Title */}
                          <Typography
                            component="div"
                            variant="body2"
                            sx={{
                              fontWeight: notification.isRead ? 500 : 600,
                              fontSize: "0.9375rem",
                              lineHeight: 1.5,
                              mb: 0.75,
                              color: "text.primary",
                            }}
                          >
                            {notification.caseTitle}
                          </Typography>

                          {/* Action Message */}
                          <Typography
                            component="div"
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontSize: "0.875rem",
                              lineHeight: 1.5,
                              mb: 1.25,
                            }}
                          >
                            {notification.message}
                          </Typography>

                          {/* Metadata Grid */}
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "repeat(2, 1fr)",
                              gap: 1.5,
                              mb: 1.25,
                              fontSize: "0.75rem",
                            }}
                          >
                            {/* Case ID */}
                            <Box>
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                }}
                              >
                                ID:{" "}
                              </Typography>
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                  fontFamily: "monospace",
                                  fontSize: "0.75rem",
                                  color: "primary.main",
                                  fontWeight: 600,
                                }}
                              >
                                {String(notification.caseId).substring(0, 8)}
                              </Typography>
                            </Box>

                            {/* Status */}
                            {notification.status !== undefined && (
                              <Box>
                                <Typography
                                  component="span"
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                    fontSize: "0.75rem",
                                    fontWeight: 500,
                                  }}
                                >
                                  Status:{" "}
                                </Typography>
                                <Chip
                                  label={getCaseStateLabel(notification.status)}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: "0.6875rem",
                                    fontWeight: 500,
                                    "& .MuiChip-label": { px: 1.5 },
                                  }}
                                />
                              </Box>
                            )}

                            {/* Rule */}
                            {notification.rule && (
                              <Box sx={{ gridColumn: "span 2" }}>
                                <Typography
                                  component="span"
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                    fontSize: "0.75rem",
                                    fontWeight: 500,
                                  }}
                                >
                                  Rule:{" "}
                                </Typography>
                                <Typography
                                  component="span"
                                  variant="caption"
                                  sx={{
                                    fontSize: "0.75rem",
                                    color: "text.primary",
                                    fontWeight: 600,
                                  }}
                                >
                                  {notification.rule}
                                </Typography>
                              </Box>
                            )}

                            {/* Chapter */}
                            {notification.chapter && (
                              <Box sx={{ gridColumn: "span 2" }}>
                                <Typography
                                  component="span"
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                    fontSize: "0.75rem",
                                    fontWeight: 500,
                                  }}
                                >
                                  Chapter:{" "}
                                </Typography>
                                <Typography
                                  component="span"
                                  variant="caption"
                                  sx={{
                                    fontSize: "0.75rem",
                                    color: "text.primary",
                                    fontWeight: 600,
                                  }}
                                >
                                  {notification.chapter}
                                </Typography>
                              </Box>
                            )}
                          </Box>

                          {/* Bottom Row: Timestamp and Priority */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              flexWrap: "wrap",
                            }}
                          >
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.75rem" }}
                            >
                              {formatDateTime(notification.timestamp)}
                            </Typography>
                            {notification.priority && (
                              <Chip
                                label={notification.priority}
                                size="small"
                                color={
                                  notification.priority === "CRITICAL"
                                    ? "error"
                                    : notification.priority === "HIGH"
                                      ? "warning"
                                      : notification.priority === "MEDIUM"
                                        ? "info"
                                        : "default"
                                }
                                sx={{
                                  height: 18,
                                  fontSize: "0.6875rem",
                                  fontWeight: 500,
                                  textTransform: "uppercase",
                                  "& .MuiChip-label": { px: 1.5 },
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      }
                    />

                    <Tooltip title="Open case">
                      <IconButton
                        size="small"
                        sx={{
                          color: "text.secondary",
                          opacity: 0,
                          ".MuiListItem-root:hover &": {
                            opacity: 1,
                          },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNotificationClick(notification);
                        }}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItem>
                  {index < filteredNotifications.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </>
        )}
      </Box>

      {/* Footer - View All */}
      {filteredNotifications.length > 0 && (
        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: "divider",
            textAlign: "center",
          }}
        >
          <Button
            fullWidth
            size="small"
            onClick={() => {
              navigate("/cases");
              onClose();
            }}
            sx={{ textTransform: "none" }}
          >
            View all open cases
          </Button>
        </Box>
      )}
    </Popover>
  );
};

export default NotificationPanel;
